import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  User as FirebaseUser, 
  signInWithPopup, 
  signOut, 
  onAuthStateChanged 
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { User, SubscriptionStatus } from '../types/user';
import { api } from '../services/api';
import { debugEnabled, dlog } from '../utils/debug';

interface AuthContextType {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  subscriptionStatus: SubscriptionStatus | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshSubscriptionStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile and subscription status
  const fetchUserProfile = async (firebaseUser: FirebaseUser) => {
    // Prevent multiple simultaneous calls for the same user
    if (user && user.firebase_uid === firebaseUser.uid) {
      if (debugEnabled) dlog('AuthContext: User profile already loaded, skipping fetch');
      return;
    }

    try {
      if (debugEnabled) dlog('AuthContext: Getting ID token for user:', firebaseUser.email);
      const token = await firebaseUser.getIdToken();
      if (debugEnabled) dlog('AuthContext: Token obtained, calling getUserProfile');
      
      const profile = await api.getUserProfile(token);
      if (debugEnabled) dlog('AuthContext: Profile received:', profile);
      setUser(profile);
      
      const subscription = await api.getSubscriptionStatus(token);
      if (debugEnabled) dlog('AuthContext: Subscription status received:', subscription);
      setSubscriptionStatus(subscription);
    } catch (error: any) {
      console.error('AuthContext: Failed to fetch user profile:', error);
      
      // If it's a 404, the backend might not be running
      if (error?.response?.status === 404) {
        console.error('Backend API not running. Please start the backend server on port 8000.');
      }
      
      setUser(null);
      setSubscriptionStatus(null);
    }
  };

  // Refresh subscription status
  const refreshSubscriptionStatus = async () => {
    if (firebaseUser) {
      try {
        const token = await firebaseUser.getIdToken();
        const subscription = await api.getSubscriptionStatus(token);
        setSubscriptionStatus(subscription);
      } catch (error) {
        console.error('Failed to refresh subscription status:', error);
      }
    }
  };

  // Sign in with Google
  const signIn = async () => {
    if (!auth || !googleProvider) {
      throw new Error('Firebase not configured. Please set up Firebase authentication.');
    }
    
    try {
      setLoading(true);
      const result = await signInWithPopup(auth, googleProvider);
      await fetchUserProfile(result.user);
    } catch (error) {
      console.error('Sign in failed:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOutUser = async () => {
    if (!auth) {
      setUser(null);
      setFirebaseUser(null);
      setSubscriptionStatus(null);
      return;
    }
    
    try {
      setLoading(true);
      await signOut(auth);
      setUser(null);
      setFirebaseUser(null);
      setSubscriptionStatus(null);
    } catch (error) {
      console.error('Sign out failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    if (!auth) {
      if (debugEnabled) dlog('AuthContext: Firebase auth not available');
      setLoading(false);
      return;
    }

    if (debugEnabled) dlog('AuthContext: Setting up auth state listener');
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (debugEnabled) dlog('AuthContext: Auth state changed, firebaseUser:', firebaseUser?.email || 'null');
      setFirebaseUser(firebaseUser);
      
      if (firebaseUser) {
        if (debugEnabled) dlog('AuthContext: Fetching user profile for:', firebaseUser.email);
        await fetchUserProfile(firebaseUser);
      } else {
        if (debugEnabled) dlog('AuthContext: No firebase user, clearing state');
        setUser(null);
        setSubscriptionStatus(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    user,
    firebaseUser,
    subscriptionStatus,
    loading,
    signIn,
    signOut: signOutUser,
    refreshSubscriptionStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
